import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { formatDateSafe, parseDateLocal } from "@/lib/utils";
import { deriveCoverageDates } from "@shared/utils/planCoverageDates";
import { pickPlanYearLink } from "@shared/utils/planYearLinks";

// Import enums and utility arrays
import {
  PlanType,
  NetworkType,
  MetalTier,
  CommissionCalculationType,
  PLAN_TYPE_OPTIONS,
  NETWORK_TYPE_OPTIONS,
  METAL_TIER_OPTIONS,
} from "../../../../../shared/schemas/plans";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SystemPlanIdBadge from "@/components/plans/SystemPlanIdBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Shield, Save, Edit, HelpCircle, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { SystemPlanCombobox } from "@/components/ui/plans/SystemPlanCombobox";
import type { PlanWithCarrier } from "../../../../../shared/schemas/plans";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const planUpdateSchema = z
  .object({
    name: z.string().min(1, "Plan name is required"),
    adminName: z.string().optional(),
    type: z.nativeEnum(PlanType),
    carrierId: z.string().min(1, "Carrier is required"),
    planCode: z.string().min(1, "Plan code is required"),
    subGroupNumber: z.string().optional(),
    systemPlanId: z.number().nullable().optional(),
    networkType: z.nativeEnum(NetworkType).optional(),
    metalTier: z.nativeEnum(MetalTier).optional(),
    description: z.string().optional(),
    renewalDate: z.string().optional(),
    // Not required here: this field's card is hidden when viewing through a
    // client link, so a plan with no effectiveDate would silently block the
    // whole form's submit on a field the broker can't see. Enforced instead
    // in handleUpdatePlan, only when the card is actually shown.
    effectiveDate: z.string().optional(),
    endDate: z.string().optional(),
    availableStartDate: z.string().optional(),
    availableEndDate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Validate effective date is 1st or 15th, and derived dates match
    if (data.effectiveDate) {
      const eff = parseDateLocal(data.effectiveDate);
      if (eff && !Number.isNaN(eff.getTime())) {
        const day = eff.getDate();
        if (day !== 1 && day !== 15) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["effectiveDate"],
            message: "Effective Date must be on the 1st or 15th",
          });
        }

        const expected = deriveCoverageDates(eff);
        const expectedEnd = expected.endDate!.split("T")[0];
        if (data.endDate && data.endDate !== expectedEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message:
              "End Date must be one year minus one day from Effective Date",
          });
        }

        const expectedAvailStart = expected.availableStartDate!.split("T")[0];
        if (
          data.availableStartDate &&
          data.availableStartDate !== expectedAvailStart
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["availableStartDate"],
            message:
              "Available Start Date must be three months prior to Effective Date",
          });
        }

        if (data.availableEndDate && data.availableEndDate !== expectedEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["availableEndDate"],
            message:
              "Available End Date must be one year minus one day from Effective Date",
          });
        }

        const expectedRenewalDate = expected.renewalDate!.split("T")[0];
        if (data.renewalDate && data.renewalDate !== expectedRenewalDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["renewalDate"],
            message:
              "Renewal Date must be exactly one year from Effective Date",
          });
        }
      }
    }
  });

type PlanUpdateForm = z.infer<typeof planUpdateSchema>;

interface PlanOverviewProps {
  plan: PlanWithCarrier;
  clientId?: string;
  onPlanUpdated?: (updatedPlan: PlanWithCarrier) => void;
}

const commissionTypeOptions = [
  { value: CommissionCalculationType.PERCENTAGE, label: "Percentage (of premium)" },
  { value: CommissionCalculationType.FLAT, label: "Flat ($/month)" },
  { value: CommissionCalculationType.PEPM, label: "PEPM ($ per enrolled employee/month)" },
  { value: CommissionCalculationType.PMPM, label: "PMPM ($ per enrolled member/month)" },
];

// Carrier-issued group/policy number for THIS client on this plan. Stored on
// the client_plans link — the shared plan row (and its planCode) is never
// touched, so other clients' numbers can't change from here.
function ClientGroupNumberField({
  link,
  clientId,
}: {
  link: { id: number; groupNumber?: string | null };
  clientId: string;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(link.groupNumber ?? "");
  useEffect(() => setValue(link.groupNumber ?? ""), [link.id, link.groupNumber]);
  const dirty = value.trim() !== (link.groupNumber ?? "");
  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", `/api/client-plans/${link.id}`, {
        groupNumber: value.trim() || null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans", clientId] });
      toast({ title: "Group number saved", description: "Applies to this client only." });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't save group number", description: e?.message, variant: "destructive" }),
  });
  return (
    <div className="flex items-center gap-2">
      <Input
        className="h-8 w-56 text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Carrier group / policy number"
      />
      {dirty && (
        <Button size="sm" className="h-8" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving…" : "Save"}
        </Button>
      )}
    </div>
  );
}

const clientCoverageSchema = z
  .object({
    effectiveDate: z.string().min(1, "Effective date is required"),
    commissionCalculationType: z
      .nativeEnum(CommissionCalculationType)
      .default(CommissionCalculationType.PERCENTAGE),
    commissionRate: z.string().optional(),
    commissionValue: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const calcType = data.commissionCalculationType || CommissionCalculationType.PERCENTAGE;
    // Blank is allowed (saved as 0 / null); the field shows MissingCommissionNote instead.
    if (calcType === CommissionCalculationType.PERCENTAGE) {
      const v = Number(data.commissionRate);
      if (data.commissionRate?.trim() && (!Number.isFinite(v) || v < 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["commissionRate"], message: "Commission rate must be a valid number" });
      }
    } else {
      const v = Number(data.commissionValue);
      if (data.commissionValue?.trim() && (!Number.isFinite(v) || v < 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["commissionValue"], message: "Commission amount must be a valid number" });
      }
    }
    const eff = parseDateLocal(data.effectiveDate);
    if (eff && !Number.isNaN(eff.getTime())) {
      const day = eff.getDate();
      if (day !== 1 && day !== 15) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["effectiveDate"], message: "Effective Date must be on the 1st or 15th" });
      }
    }
  });

type ClientCoverageForm = z.infer<typeof clientCoverageSchema>;

function MissingCommissionNote() {
  return (
    <p className="text-xs text-amber-600 dark:text-amber-500">
      No commission amount entered. The Commissions section won't calculate correctly for this plan until one is set.
    </p>
  );
}

export interface ClientCoverageFieldsHandle {
  /**
   * Saves only if the broker actually changed something here. Returns false
   * when there were changes but they failed validation (or the save
   * itself failed) - the caller must NOT exit edit mode in that case, or
   * the error the broker just saw disappears with no chance to fix it.
   */
  submitIfDirty: () => Promise<boolean>;
}

/**
 * This client's own coverage dates and commission for this plan — lives on
 * the client_plans link, never on the shared plan row. A platform-scope
 * plan's own effectiveDate/commissionRate stay locked (Plan Library-owned,
 * same as Name/Carrier/Plan Code); this is what a broker edits instead.
 *
 * No Save button of its own - the single "Save Changes" button on the card
 * drives both this and the plan form together (via the exposed ref), so
 * there's one save action, not two.
 */
const ClientCoverageFields = forwardRef<
  ClientCoverageFieldsHandle,
  {
    link: {
      id: number;
      effectiveDate?: string | Date | null;
      commissionCalculationType?: string | null;
      commissionRate?: string | null;
      commissionValue?: string | null;
    };
    clientId: string;
  }
>(function ClientCoverageFields({ link, clientId }, ref) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isEmployer = (user as any)?.role === "employer";
  const queryClient = useQueryClient();
  const toDateInputValue = (value: any) => {
    if (!value) return "";
    const d = parseDateLocal(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };
  const defaults: ClientCoverageForm = {
    effectiveDate: toDateInputValue(link.effectiveDate),
    commissionCalculationType:
      (link.commissionCalculationType as CommissionCalculationType) || CommissionCalculationType.PERCENTAGE,
    commissionRate: link.commissionRate || "",
    commissionValue: link.commissionValue || "",
  };
  const form = useForm<ClientCoverageForm>({
    resolver: zodResolver(clientCoverageSchema),
    defaultValues: defaults,
  });
  useEffect(() => form.reset(defaults), [link.id, link.effectiveDate, link.commissionRate, link.commissionCalculationType, link.commissionValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same derivation as useClientPlans.ts (feeds the employee's actual
  // open-enrollment gating) - shown here so the broker sees what enrollment
  // will actually use, tied to THIS client's effective date, not the plan's.
  const derivedDates = deriveCoverageDates(parseDateLocal(form.watch("effectiveDate")));
  const endDate = derivedDates.endDate?.split("T")[0] || "";
  const renewalDate = derivedDates.renewalDate?.split("T")[0] || "";
  const availableStartDate = derivedDates.availableStartDate?.split("T")[0] || "";

  // Answers the 409+requiresConfirmation the PUT below can throw when
  // effectiveDate changes with enrollments in place.
  const [pendingConfirm, setPendingConfirm] = useState<{
    affectedCount: number;
    affectedDate: string | Date | null;
    affectedEndDate: string | Date | null;
    newEffectiveDate: string | Date | null;
    newEndDate: string | Date | null;
    resolve: (move: boolean) => void;
  } | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (data: ClientCoverageForm) => {
      const calcType = data.commissionCalculationType || CommissionCalculationType.PERCENTAGE;
      const buildBody = (moveEnrollments?: boolean) => ({
        effectiveDate: data.effectiveDate,
        endDate: endDate || null,
        renewalDate: renewalDate || null,
        commissionCalculationType: calcType,
        commissionRate: calcType === CommissionCalculationType.PERCENTAGE ? (data.commissionRate || "0") : "0",
        commissionValue: calcType === CommissionCalculationType.PERCENTAGE ? null : data.commissionValue || null,
        ...(moveEnrollments ? { moveEnrollments: true } : {}),
      });
      try {
        const res = await apiRequest("PUT", `/api/client-plans/${link.id}`, buildBody());
        return res.json();
      } catch (err: any) {
        if (!err?.details?.requiresConfirmation) throw err;
        const move = await new Promise<boolean>((resolve) => {
          setPendingConfirm({
            affectedCount: err.details.affectedCount,
            affectedDate: err.details.affectedDate,
            affectedEndDate: err.details.affectedEndDate,
            newEffectiveDate: err.details.newEffectiveDate,
            newEndDate: err.details.newEndDate,
            resolve,
          });
        });
        setPendingConfirm(null);
        if (!move) {
          // Revert just the date so Save doesn't re-offer this warning.
          form.setValue("effectiveDate", defaults.effectiveDate, { shouldDirty: false });
          return { cancelled: true } as const;
        }
        const res = await apiRequest("PUT", `/api/client-plans/${link.id}`, buildBody(true));
        return res.json();
      }
    },
    onSuccess: (result: any) => {
      if (result?.cancelled) return; // broker cancelled the date change - nothing to report
      queryClient.invalidateQueries({ queryKey: ["/api/client-plans", clientId] });
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/missing-commission-plans"] });
      toast({ title: "Coverage saved", description: "Applies to this client only." });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't save coverage", description: e?.message, variant: "destructive" }),
  });

  useImperativeHandle(ref, () => ({
    submitIfDirty: async () => {
      // formState.isDirty is never read in this component's render body, so
      // RHF never subscribes to it and it stays stuck at false (a documented
      // RHF gotcha - formState props are lazily subscribed). dirtyFields
      // isn't subject to that.
      const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
      if (!isDirty) return true;
      let wasValid = false;
      await form.handleSubmit(async (data) => {
        wasValid = true;
        try {
          const result = await saveMutation.mutateAsync(data);
          // Cancelled date change - nothing saved, so stay in edit mode.
          if ((result as any)?.cancelled) wasValid = false;
        } catch {
          // onError above already toasts - swallow so the validity signal
          // below still reflects "validation passed" vs "was invalid".
        }
      })();
      return wasValid;
    },
  }));

  return (
    <Form {...form}>
      <>
        <Card className="bg-muted/60">
          <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
            <CardTitle className="text-base font-semibold">Coverage Period</CardTitle>
            {!isEmployer && (
              <CardDescription className="text-xs">This client's plan year</CardDescription>
            )}
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="effectiveDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective Date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <Label className="flex items-center gap-2">
                  End Date
                  <Badge variant="outline" className="text-[10px]">Auto calculated</Badge>
                </Label>
                <Input value={endDate} type="date" disabled className="mt-2" />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Renewal Date
                  <Badge variant="outline" className="text-[10px]">Auto calculated</Badge>
                </Label>
                <Input value={renewalDate} type="date" disabled className="mt-2" />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Available Start Date
                  <Badge variant="outline" className="text-[10px]">Auto calculated</Badge>
                </Label>
                <Input value={availableStartDate} type="date" disabled className="mt-2" />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  Available End Date
                  <Badge variant="outline" className="text-[10px]">Auto calculated</Badge>
                </Label>
                <Input value={endDate} type="date" disabled className="mt-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-muted/60">
          <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
            <CardTitle className="text-base font-semibold">Commission</CardTitle>
            <CardDescription className="text-xs">Broker compensation</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="commissionCalculationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Commission Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select commission type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {commissionTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("commissionCalculationType") === CommissionCalculationType.PERCENTAGE ? (
                <FormField
                  control={form.control}
                  name="commissionRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Commission Rate (%)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" />
                      </FormControl>
                      {!field.value?.trim() && <MissingCommissionNote />}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="commissionValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Commission Amount ($)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" min="0" />
                      </FormControl>
                      {!field.value?.trim() && <MissingCommissionNote />}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dismissing (Esc/overlay click) counts as Cancel too. */}
        <AlertDialog
          open={!!pendingConfirm}
          onOpenChange={(open) => {
            if (!open) pendingConfirm?.resolve(false);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Move enrolled employees' dates too?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingConfirm?.affectedCount} employee(s) are enrolled with a{" "}
                {formatDateSafe(pendingConfirm?.affectedDate)} start
                {pendingConfirm?.affectedEndDate ? ` (ending ${formatDateSafe(pendingConfirm.affectedEndDate)})` : ""}.
                Moving them will shift both dates to match the plan's new{" "}
                {formatDateSafe(pendingConfirm?.newEffectiveDate)} start
                {pendingConfirm?.newEndDate ? ` (ending ${formatDateSafe(pendingConfirm.newEndDate)})` : ""}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => pendingConfirm?.resolve(false)}>
                Cancel date change
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => pendingConfirm?.resolve(true)}>
                Move their enrollments
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    </Form>
  );
});

export function PlanOverview({ plan, clientId, onPlanUpdated }: PlanOverviewProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isEmployer = (user as any)?.role === "employer";
  const queryClient = useQueryClient();
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  // One "Save Changes" button drives both this plan's own form AND the
  // client-coverage form below (see handleUpdatePlan) - no second button.
  const coverageFieldsRef = useRef<ClientCoverageFieldsHandle>(null);
  // Platform-owned identity fields (Name/Carrier/Plan Code) stay read-only
  // on a platform-scope plan for everyone, including superadmin - unlike
  // permissions.canEditDetails (which computePlanAccess always grants to
  // superadmin), this checks scope directly so it can't be bypassed by role.
  const canEditDetails = !((plan as any)?.clientId == null && (plan as any)?.agencyId == null);
  const isPlatformScope = !canEditDetails;

  // Commission now lives on the client-plan link (this client's own rate),
  // falling back to the plan's default when this client has no override -
  // same precedence CommissionManagementTab uses when seeding a new
  // commission line. Never reads/writes the shared plan row from here.
  const getCommissionTypeLabel = () => {
    const calcType =
      (clientLink?.commissionCalculationType as CommissionCalculationType) ||
      ((plan as any).commissionCalculationType as CommissionCalculationType) ||
      CommissionCalculationType.PERCENTAGE;
    return (
      commissionTypeOptions.find((o) => o.value === calcType)?.label ||
      "Percentage (of premium)"
    );
  };

  const formatCommissionSummary = () => {
    const calcType =
      (clientLink?.commissionCalculationType as CommissionCalculationType) ||
      ((plan as any).commissionCalculationType as CommissionCalculationType) ||
      CommissionCalculationType.PERCENTAGE;
    const valueRaw = clientLink?.commissionValue ?? (plan as any).commissionValue;
    const valueNum =
      valueRaw === undefined || valueRaw === null || valueRaw === ""
        ? null
        : Number(valueRaw);

    if (calcType === CommissionCalculationType.PERCENTAGE) {
      const rateRaw = clientLink?.commissionRate ?? plan.commissionRate;
      const rateNum =
        rateRaw === undefined || rateRaw === null || String(rateRaw).trim() === ""
          ? null
          : Number(rateRaw);
      if (rateNum == null || !Number.isFinite(rateNum)) return "Not set";
      return `${rateNum}%`;
    }

    const dollars =
      valueNum != null && Number.isFinite(valueNum)
        ? `$${valueNum.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : "Not set";

    // Type is displayed separately in the read-only view; only show the numeric amount here.
    if (
      calcType === CommissionCalculationType.FLAT ||
      calcType === CommissionCalculationType.PEPM ||
      calcType === CommissionCalculationType.PMPM
    ) {
      return dollars;
    }

    return "Not set";
  };

  // Fetch carriers for the edit form's dropdown — only while editing, so just
  // viewing the plan (e.g. an employer) doesn't fire an unnecessary request
  // (and avoids a carriers:view 403 for roles that can't list carriers).
  const { data: carriers = [] } = useQuery({
    queryKey: ["/api/carriers"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/carriers");
      return response.json();
    },
    enabled: isEditingPlan,
  });

  // Resolve the matched system/library plan's name for the read-only view.
  // A platform-scope plan IS a library plan itself, so systemPlanId (which
  // only ever gets set for an agency-owned plan matched to one) is never
  // populated for it - display it as matched to itself rather than "not
  // matched." Display only: the underlying column is left untouched.
  const matchedSystemPlanId = ((plan as any).systemPlanId as number | null | undefined) ??
    (isPlatformScope ? plan.id : null);
  const { data: libraryPlans } = useQuery<any[]>({
    queryKey: ["/api/plans", { scope: "library" }],
    enabled: matchedSystemPlanId != null && !isEditingPlan,
  });
  const matchedSystemPlan =
    matchedSystemPlanId != null
      ? libraryPlans?.find((p) => p.id === matchedSystemPlanId)
      : null;

  // This client's link to the plan — carries the per-client carrier group
  // number (client_plans.groupNumber). Shares the query key used elsewhere in
  // PlanDetail so invalidations line up.
  const { data: clientLinks = [] } = useQuery<any[]>({
    queryKey: ["/api/client-plans", clientId],
    queryFn: async () => {
      const res = await fetch(`/api/client-plans?clientId=${clientId}`, { credentials: "include" });
      return res.ok ? res.json() : [];
    },
    enabled: !!clientId,
  });
  // A renewed plan has a link per plan year. ?clientPlanId (set by the plan
  // list's View Details) names the plan year being viewed/edited; without it,
  // the plan year in force today.
  const requestedLinkId = Number(new URLSearchParams(window.location.search).get("clientPlanId"));
  const planLinks = clientLinks.filter((l: any) => l.planId === plan.id);
  const activePlanLinks = planLinks.filter((l: any) => l.isActive !== false);
  const clientLink = clientId
    ? planLinks.find((l: any) => l.id === requestedLinkId) ??
      pickPlanYearLink(activePlanLinks.length > 0 ? activePlanLinks : planLinks)
    : null;
  const otherPlanYears = clientLink ? planLinks.filter((l: any) => l.id !== clientLink.id) : [];

  // Form setup
  const toDateInputValue = (value: any) => {
    if (!value) return "";
    const d = parseDateLocal(value);
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const planForm = useForm<PlanUpdateForm>({
    resolver: zodResolver(planUpdateSchema),
    defaultValues: {
      name: plan.name || "",
      adminName: (plan as any).adminName || "",
      type: plan.type as PlanType,
      carrierId: plan.carrierId?.toString() || "",
      planCode: plan.planCode || "",
      subGroupNumber: (plan as any).subGroupNumber || "",
      systemPlanId: (plan as any).systemPlanId ?? null,
      networkType: (plan.networkType as NetworkType) || undefined,
      metalTier: (plan.metalTier as MetalTier) || undefined,
      description: plan.description || "",
      renewalDate: toDateInputValue((plan as any).renewalDate),
      effectiveDate: toDateInputValue((plan as any).effectiveDate),
      endDate: toDateInputValue((plan as any).endDate),
      availableStartDate: toDateInputValue((plan as any).availableStartDate),
      availableEndDate: toDateInputValue((plan as any).availableEndDate),
    },
  });

  // Auto-calculate dependent dates and renewal month when effective date changes
  useEffect(() => {
    const subscription = planForm.watch((value, { name }) => {
      if (name === "effectiveDate" && value.effectiveDate) {
        const eff = parseDateLocal(value.effectiveDate);
        const derived = deriveCoverageDates(eff);
        if (derived.endDate) {
          planForm.setValue("endDate", derived.endDate.split("T")[0], { shouldValidate: true });
          planForm.setValue("availableStartDate", derived.availableStartDate!.split("T")[0], {
            shouldValidate: true,
          });
          planForm.setValue("availableEndDate", derived.availableEndDate!.split("T")[0], {
            shouldValidate: true,
          });
          planForm.setValue("renewalDate", derived.renewalDate!.split("T")[0], { shouldValidate: true });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [planForm]);

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: async (data: PlanUpdateForm) => {
      const processedData = {
        name: data.name,
        adminName: data.adminName || null,
        type: data.type,
        carrierId: parseInt(data.carrierId),
        planCode: data.planCode,
        subGroupNumber: data.subGroupNumber || null,
        systemPlanId: data.systemPlanId ?? null,
        networkType: data.networkType || null,
        metalTier: data.metalTier || null,
        description: data.description || null,
        renewalDate: data.renewalDate || "",
        effectiveDate: data.effectiveDate || "",
        endDate: data.endDate || "",
        availableStartDate: data.availableStartDate || "",
        availableEndDate: data.availableEndDate || "",
      };

      const response = await apiRequest(
        "PUT",
        `/api/plans/${plan.id}`,
        processedData
      );
      return response.json();
    },
    onSuccess: async (updatedPlan) => {
      // Find the carrier name from the carriers list to preserve the relation
      const selectedCarrier = carriers.find(
        (carrier: any) => carrier.id === updatedPlan.carrierId
      );

      // Create complete plan object with carrier name
      const planWithCarrier = {
        ...updatedPlan,
        carrier: selectedCarrier?.name || plan.carrier, // Fallback to current carrier name
      };

      // Immediately update React Query cache for instant UI update
      queryClient.setQueryData([`/api/plans/${plan.id}`], planWithCarrier);

      queryClient.invalidateQueries({ queryKey: ["/api/plans"] });

      setIsEditingPlan(false);

      // Update parent with complete plan data
      onPlanUpdated?.(planWithCarrier);

      toast({
        title: "Plan Updated",
        description: "Plan details have been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update plan. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleUpdatePlan = async (data: PlanUpdateForm) => {
    // Mirrors the `!(clientLink && clientId)` guard on the Effective Date
    // card below - only require it when that card is actually shown.
    const showsOwnCoveragePeriod = !(clientLink && clientId);
    if (showsOwnCoveragePeriod && !data.effectiveDate) {
      planForm.setError("effectiveDate", { message: "Effective date is required" });
      toast({ title: "Error", description: "Effective date is required.", variant: "destructive" });
      return;
    }
    // Only PUT the plan if something in THIS form actually changed - a
    // coverage/commission-only edit on a platform-scope plan would otherwise
    // always 403 (the whole endpoint is blocked for a broker there), even
    // though nothing about the plan itself was touched.
    // Same RHF isDirty gotcha as ClientCoverageFields above.
    const planFormDirty = Object.keys(planForm.formState.dirtyFields).length > 0;
    if (planFormDirty) {
      updatePlanMutation.mutate(data);
    }
    const coverageOk = await coverageFieldsRef.current?.submitIfDirty();
    // updatePlanMutation's own onSuccess exits edit mode when it runs; if it
    // didn't run (coverage-only edit), exit here - but not if the coverage
    // form failed validation, or that error disappears before it's seen.
    if (!planFormDirty && coverageOk !== false) {
      setIsEditingPlan(false);
    }
  };

  // Save validates the whole plan form before anything is sent. In the client
  // view the plan's own coverage dates are hidden (the client's plan-year
  // dates are edited instead), so an error on one of them blocked the save
  // with nothing on screen: the client's commission / coverage edit was
  // silently dropped. Errors on fields the broker can't see no longer stop
  // the client-level save, and every other error is said out loud.
  const handleInvalidPlanForm = async (errors: Record<string, any>) => {
    const hiddenPlanDates = ["effectiveDate", "endDate", "availableStartDate", "availableEndDate", "renewalDate"];
    const fields = Object.keys(errors);
    const firstMessage = String(errors[fields[0]]?.message || "Check the highlighted fields.");
    const onlyHiddenDates = !!(clientLink && clientId) && fields.every((f) => hiddenPlanDates.includes(f));
    if (!onlyHiddenDates) {
      toast({ title: "Couldn't save", description: firstMessage, variant: "destructive" });
      return;
    }
    const coverageOk = await coverageFieldsRef.current?.submitIfDirty();
    if (Object.keys(planForm.formState.dirtyFields).length > 0) {
      toast({
        title: "Plan details not saved",
        description: `The plan's own coverage dates need fixing first (${firstMessage}). Changes to this client's coverage and commission were saved.`,
        variant: "destructive",
      });
      return;
    }
    if (coverageOk !== false) setIsEditingPlan(false);
  };

  // Reset form when plan data changes or when entering edit mode
  useEffect(() => {
    if (isEditingPlan && plan) {
      planForm.reset({
        name: plan.name || "",
        adminName: (plan as any).adminName || "",
        type: plan.type as PlanType,
        carrierId: plan.carrierId?.toString() || "",
        planCode: plan.planCode || "",
        subGroupNumber: (plan as any).subGroupNumber || "",
        systemPlanId: (plan as any).systemPlanId ?? null,
        networkType: (plan.networkType as NetworkType) || undefined,
        metalTier: (plan.metalTier as MetalTier) || undefined,
        description: plan.description || "",
        renewalDate: toDateInputValue((plan as any).renewalDate),
        effectiveDate: toDateInputValue((plan as any).effectiveDate),
        endDate: toDateInputValue((plan as any).endDate),
        availableStartDate: toDateInputValue((plan as any).availableStartDate),
        availableEndDate: toDateInputValue((plan as any).availableEndDate),
      });
    }
  }, [isEditingPlan, plan, planForm]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <Shield className="h-5 w-5" />
            Plan Details
            <SystemPlanIdBadge planId={plan.id} />
          </CardTitle>
          <div className="flex items-center gap-2">
            {isEditingPlan ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsEditingPlan(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={planForm.handleSubmit(handleUpdatePlan, handleInvalidPlanForm)}
                  disabled={updatePlanMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updatePlanMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditingPlan(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Plan
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isEditingPlan ? (
          <Form {...planForm}>
            <TooltipProvider>
              <form
                onSubmit={planForm.handleSubmit(handleUpdatePlan, handleInvalidPlanForm)}
                className="space-y-4"
              >
                {/* No client context (e.g. viewing from the agency's own
                    Plan Library, not through a client) - only System Plan
                    renders, so skip the 2-col grid entirely instead of
                    leaving half the row empty next to it. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  <Card className="bg-muted/60">
                    {!(isEmployer && isPlatformScope) && (
                    <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                      <CardTitle className="text-base font-semibold">
                        {isPlatformScope ? "System Plan" : "Plan Definition"}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {isPlatformScope
                          ? "Shared by every client on this plan"
                          : "Set by your agency"}
                      </CardDescription>
                    </CardHeader>
                    )}
                    <CardContent className={`px-4 pb-4 ${isEmployer && isPlatformScope ? "pt-4" : "pt-0"}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={planForm.control}
                        name="systemPlanId"
                        render={({ field }) => {
                          const planType = planForm.watch("type");
                          // Display only: a platform-scope plan IS the library
                          // plan, so show it matched to itself instead of "not
                          // matched" when the column itself is unset. Doesn't
                          // touch field.value/onChange, so nothing is written
                          // unless the broker explicitly changes the match.
                          const displayValue = field.value ?? (isPlatformScope ? plan.id : null);
                          return (
                            <FormItem>
                              <FormLabel>Matched System Plan (Optional)</FormLabel>
                              {canEditDetails ? (
                                <>
                                  <SystemPlanCombobox
                                    value={displayValue}
                                    typeFilter={
                                      planType === PlanType.HEALTH ||
                                      planType === PlanType.MEDICAL
                                        ? [PlanType.HEALTH, PlanType.MEDICAL]
                                        : planType
                                          ? [planType]
                                          : undefined
                                    }
                                    onSelect={(p) => field.onChange(p?.id ?? null)}
                                    placeholder="Match to a library plan..."
                                  />
                                  {displayValue == null && (
                                    <Alert
                                      variant="default"
                                      className="mt-2 border-amber-300 bg-amber-50"
                                    >
                                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                                      <AlertDescription className="text-xs text-amber-800">
                                        Not matched to a system plan — enrollment forms may
                                        not auto-check the plan selection.
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                </>
                              ) : (
                                <FormControl>
                                  <Input
                                    disabled
                                    value={
                                      displayValue != null
                                        ? matchedSystemPlan
                                          ? `${matchedSystemPlan.name}${matchedSystemPlan.planCode ? ` (${matchedSystemPlan.planCode})` : ""}`
                                          : `Plan #${displayValue}`
                                        : "Not matched"
                                    }
                                  />
                                </FormControl>
                              )}
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                      <FormField
                        control={planForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Plan Name</FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!canEditDetails} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="adminName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Admin Name (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                disabled={!canEditDetails}
                                placeholder="Internal/admin-facing name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="carrierId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Carrier</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={!canEditDetails}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select carrier" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {Array.isArray(carriers) &&
                                  carriers.map((carrier: any) => (
                                    <SelectItem
                                      key={carrier.id}
                                      value={carrier.id.toString()}
                                    >
                                      {carrier.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="planCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Plan Code (plan identifier)</FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!canEditDetails} />
                            </FormControl>
                            <FormDescription>
                              The plan's own stable identifier, shared by every client on this
                              plan.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Plan Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={!canEditDetails}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select plan type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {PLAN_TYPE_OPTIONS.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="networkType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Network Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value || ""}
                              disabled={!canEditDetails}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select network type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {NETWORK_TYPE_OPTIONS.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="metalTier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Metal Tier</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value || ""}
                              disabled={!canEditDetails}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select tier" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {METAL_TIER_OPTIONS.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="subGroupNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sub-Group Number (Optional)</FormLabel>
                            <FormControl>
                              <Input {...field} disabled={!canEditDetails} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={planForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel>Plan Description (Optional)</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Describe the plan features, benefits, and coverage details..."
                                className="min-h-20"
                                disabled={!canEditDetails}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    </CardContent>
                  </Card>

                  {!(clientLink && clientId) && (
                    <Card className="bg-muted/60">
                      <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                        <CardTitle className="text-base font-semibold">Coverage Period</CardTitle>
                        <CardDescription className="text-xs">Plan year</CardDescription>
                      </CardHeader>
                      <CardContent className="px-4 pb-4 pt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="col-span-1 md:col-span-2 text-xs text-gray-500">
                            End Date, Available Start Date, Available End Date, and
                            Renewal Date are auto-calculated from Effective Date.
                          </div>
                          <FormField
                            control={planForm.control}
                            name="effectiveDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Effective Date</FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" disabled={!canEditDetails} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={planForm.control}
                            name="availableStartDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                  Available Start Date
                                  <Badge variant="outline" className="text-[10px]">
                                    Auto calculated
                                  </Badge>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-muted-foreground">
                                        <HelpCircle className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Three months before Effective Date.
                                    </TooltipContent>
                                  </Tooltip>
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" disabled />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={planForm.control}
                            name="availableEndDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                  Available End Date
                                  <Badge variant="outline" className="text-[10px]">
                                    Auto calculated
                                  </Badge>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-muted-foreground">
                                        <HelpCircle className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Same as End Date (one year minus one day from
                                      Effective Date).
                                    </TooltipContent>
                                  </Tooltip>
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" disabled />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={planForm.control}
                            name="endDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                  End Date
                                  <Badge variant="outline" className="text-[10px]">
                                    Auto calculated
                                  </Badge>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-muted-foreground">
                                        <HelpCircle className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      One year minus one day from Effective Date.
                                    </TooltipContent>
                                  </Tooltip>
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" disabled />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={planForm.control}
                            name="renewalDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                  Renewal Date
                                  <Badge variant="outline" className="text-[10px]">
                                    Auto calculated
                                  </Badge>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help text-muted-foreground">
                                        <HelpCircle className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Exactly one year from Effective Date.
                                    </TooltipContent>
                                  </Tooltip>
                                </FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" disabled />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {clientLink && clientId && (
                    <ClientCoverageFields ref={coverageFieldsRef} link={clientLink} clientId={clientId} />
                  )}
                  {clientLink && clientId && (
                    <Card className="bg-muted/60">
                      <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                        <CardTitle className="text-base font-semibold">
                          {isEmployer ? "Group Number" : "This Client"}
                        </CardTitle>
                        {!isEmployer && (
                          <CardDescription className="text-xs">
                            Specific to this client's setup
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="px-4 pb-4 pt-0">
                        {isEmployer ? (
                          <ClientGroupNumberField link={clientLink} clientId={clientId} />
                        ) : (
                          <div>
                            <Label>Group Number (carrier — this client only)</Label>
                            <div className="mt-2">
                              <ClientGroupNumberField link={clientLink} clientId={clientId} />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </form>
            </TooltipProvider>
          </Form>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <Card className="bg-muted/60">
              {!(isEmployer && isPlatformScope) && (
              <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                <CardTitle className="text-base font-semibold">
                  {isPlatformScope ? "System Plan" : "Plan Definition"}
                </CardTitle>
                <CardDescription className="text-xs">
                  {isPlatformScope
                    ? "Shared by every client on this plan"
                    : "Set by your agency"}
                </CardDescription>
              </CardHeader>
              )}
              <CardContent className={`px-4 pb-4 space-y-4 ${isEmployer && isPlatformScope ? "pt-4" : "pt-0"}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Matched System Plan
                    </Label>
                    <p className="text-sm">
                      {matchedSystemPlanId != null
                        ? matchedSystemPlan
                          ? `${matchedSystemPlan.name}${matchedSystemPlan.planCode ? ` (${matchedSystemPlan.planCode})` : ""}`
                          : `Plan #${matchedSystemPlanId}`
                        : "Not matched"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Plan Name
                    </Label>
                    <p className="text-sm">{plan.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Admin Name
                    </Label>
                    <p className="text-sm">
                      {(plan as any).adminName || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Carrier
                    </Label>
                    <p className="text-sm">{plan.carrier}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Plan Code (plan identifier)
                    </Label>
                    <p className="text-sm">{plan.planCode}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Plan Type
                    </Label>
                    <p className="text-sm capitalize">{plan.type}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Network Type
                    </Label>
                    <p className="text-sm">
                      {plan.networkType || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Metal Tier
                    </Label>
                    <p className="text-sm capitalize">
                      {plan.metalTier || "Not applicable"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Sub-Group Number
                    </Label>
                    <p className="text-sm">
                      {(plan as any).subGroupNumber || "Not specified"}
                    </p>
                  </div>
                </div>
              </div>
              {plan.description && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Description
                  </Label>
                  <p className="text-sm mt-2">{plan.description}</p>
                </div>
              )}
              </CardContent>
            </Card>

            {!(clientLink && clientId) && (
              <Card className="bg-muted/60">
                <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                  <CardTitle className="text-base font-semibold">Coverage Period</CardTitle>
                  <CardDescription className="text-xs">Plan year</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Effective Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe((plan as any).effectiveDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        End Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe((plan as any).endDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Renewal Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe((plan as any).renewalDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Available Start Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe((plan as any).availableStartDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Available End Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe((plan as any).availableEndDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {!(clientLink && clientId) && (
              <Card className="bg-muted/60">
                <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                  <CardTitle className="text-base font-semibold">Commission</CardTitle>
                  <CardDescription className="text-xs">Broker compensation</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Commission Type
                      </Label>
                      <p className="text-sm">{getCommissionTypeLabel()}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Commission
                      </Label>
                      <p className="text-sm">{formatCommissionSummary()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {clientLink && clientId && (
              <Card className="bg-muted/60">
                <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                  <CardTitle className="text-base font-semibold">Coverage Period</CardTitle>
                  {!isEmployer && (
                    <CardDescription className="text-xs">This client's plan year</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  {otherPlanYears.length > 0 && (
                    <p className="mb-3 text-xs text-muted-foreground">
                      Other plan years:{" "}
                      {otherPlanYears.map((l: any, i: number) => (
                        <span key={l.id}>
                          {i > 0 && ", "}
                          <a
                            className="text-blue-600 hover:underline"
                            href={`/plans/${plan.id}?clientId=${clientId}&clientPlanId=${l.id}`}
                          >
                            {formatDateSafe(l.effectiveDate)}
                          </a>
                        </span>
                      ))}
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Effective Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe(clientLink.effectiveDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        End Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe(clientLink.endDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Renewal Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe(
                            deriveCoverageDates(parseDateLocal(clientLink.effectiveDate)).renewalDate,
                          );
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Available Start Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe(
                            deriveCoverageDates(parseDateLocal(clientLink.effectiveDate)).availableStartDate,
                          );
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Available End Date
                      </Label>
                      <p className="text-sm">
                        {(() => {
                          const formatted = formatDateSafe(clientLink.endDate);
                          return formatted === "TBD" ? "Not set" : formatted;
                        })()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {clientLink && clientId && (
              <Card className="bg-muted/60">
                <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                  <CardTitle className="text-base font-semibold">Commission</CardTitle>
                  <CardDescription className="text-xs">Broker compensation</CardDescription>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Commission Type
                      </Label>
                      <p className="text-sm">{getCommissionTypeLabel()}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Commission
                      </Label>
                      <p className="text-sm">{formatCommissionSummary()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {clientLink && clientId && (
              <Card className="bg-muted/60">
                <CardHeader className="flex flex-row items-center justify-between gap-2 py-3 px-4 space-y-0">
                  <CardTitle className="text-base font-semibold">
                    {isEmployer ? "Group Number" : "This Client"}
                  </CardTitle>
                  {!isEmployer && (
                    <CardDescription className="text-xs">
                      Specific to this client's setup
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  {isEmployer ? (
                    <p className="text-sm">{clientLink.groupNumber || "Not specified"}</p>
                  ) : (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">
                        Group Number (carrier — this client only)
                      </Label>
                      <p className="text-sm">{clientLink.groupNumber || "Not specified"}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
